import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import QuestionReadyActions from "../../../../components/anonymous/QuestionReadyActions";
import QuestionPreviewBox from "../../../../components/shared/QuestionPreviewBox";
import VideoAnswerModal from "../../../../components/anonymous/VideoAnswerModal";
import VoiceAnswerModal from "../../../../components/anonymous/VoiceAnswerModal";
import { downloadQuestionAsImage } from "../../../../utils/downloadUtils";
import { STORY_CONTENT_WIDTH } from "../../../../utils/mediaExportUtils";
import BackgroundSelectionModal from "../../../../components/anonymous/BackgroundSelectionModal";
import { showToast } from "../../../../utils/toastUtils";
import {
  StyledSheetBackdrop,
  StyledSheetPanel,
} from "./AnswerDetailSheet.styled";

type AnswerDetailSheetProps = {
  displayNumber: number;
  questionText: string;
  boxTitle: string;
  boxShortCode: string;
  onClose: () => void;
  onStatusChange?: (status: "viewed" | "answered") => void;
};

const AnswerDetailSheet = ({
  displayNumber,
  questionText,
  boxTitle,
  boxShortCode,
  onClose,
  onStatusChange,
}: AnswerDetailSheetProps) => {
  const [isBackgroundModalOpen, setIsBackgroundModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
  const [personalAnswerText, setPersonalAnswerText] = useState("");
  const exportRef = useRef<HTMLDivElement>(null);
  const root = typeof document !== "undefined" ? document.body : null;

  const exportId = `${boxShortCode}-${displayNumber}`;
  const footerText = personalAnswerText.trim() ? personalAnswerText : undefined;

  const handleExport = () => {
    if (exportRef.current) {
      void downloadQuestionAsImage(exportRef.current, exportId).then(() => {
        showToast("התמונה הורדה בהצלחה!");
        onStatusChange?.("answered");
      });
    }
  };

  const handleDownloaded = () => {
    onStatusChange?.("answered");
  };

  const sheet = (
    <>
      <StyledSheetBackdrop onClick={onClose} />
      <StyledSheetPanel role="dialog" aria-modal="true">
        <h3 className="sheet-title">תגובה אנונימית: {displayNumber}</h3>
        <div className="sheet-body-box">
          <div className="sheet-body-inner">
            <p className="sheet-body-text">{questionText}</p>
          </div>
        </div>

        <QuestionReadyActions
          onExport={handleExport}
          onChooseBackground={() => setIsBackgroundModalOpen(true)}
          onVideoAnswer={() => setIsVideoModalOpen(true)}
          onVoiceAnswer={() => setIsVoiceModalOpen(true)}
        />

        {/* Hidden preview for export */}
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            top: "-9999px",
            width: `${STORY_CONTENT_WIDTH}px`,
          }}
        >
          <div ref={exportRef} className="question-detail-content">
            <QuestionPreviewBox title={boxTitle} footerText={footerText}>
              <div style={{ whiteSpace: "pre-wrap" }}>{questionText}</div>
            </QuestionPreviewBox>
          </div>
        </div>

        <BackgroundSelectionModal
          isOpen={isBackgroundModalOpen}
          onClose={() => setIsBackgroundModalOpen(false)}
          elementRef={exportRef}
          id={exportId}
          cardPosition="top"
          showPersonalTextInput
          personalText={personalAnswerText}
          onPersonalTextChange={setPersonalAnswerText}
          onDownloaded={() => {
            showToast("התמונה הורדה בהצלחה!");
            handleDownloaded();
          }}
        />

        <VideoAnswerModal
          isOpen={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          boxTitle={boxTitle}
          questionText={questionText}
          footerText={footerText}
          exportId={exportId}
          onDownloaded={handleDownloaded}
        />

        <VoiceAnswerModal
          isOpen={isVoiceModalOpen}
          onClose={() => setIsVoiceModalOpen(false)}
          boxTitle={boxTitle}
          questionText={questionText}
          footerText={footerText}
          exportId={exportId}
          onDownloaded={handleDownloaded}
        />
      </StyledSheetPanel>
    </>
  );

  return root ? createPortal(sheet, root) : null;
};

export default AnswerDetailSheet;
